/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable quote-props */

declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface InterfaceTimetableSearchOptions {
  origin: string;
  destination: string;
  results: number;
  via?: string;
  bicycle: boolean;
  accessibility: boolean;
}

interface InterfaceTimetableSearch {
  'event': string;
  'search_results': number;
  'search_term': string;
  'original_search_term': string;
  'additional_info': string;
}

interface InterfaceOpenLevelTwoOptions {
  opening: boolean;
  title: string;
  changes: number;
  duration: string;
  price?: string;
}

interface InterfaceOpenLevelTwo {
  'event': string;
  'type': string;
  'label': string;
  'selection'?: string;
  'additional_info': string;
  'text': string;
}

interface InterfaceBuyButtonClickOptions {
  title: string;
  link: string;
  changes: number;
  duration: string;
  price?: string;
}

interface InterfaceBuyButtonClick {
  'event': string;
  'text': string;
  'link_url': string;
  'label': string;
  'additional_info': string;
  'selection'?: string;
  'type': string;
}

interface InterfacePreviousNextConnectionsOptions {
  isForPreviousConnections: boolean;
}

interface InterfacePreviousNextConnections {
  'event': string;
  'type': string;
  'label': string;
}

export class Tracking {
  private _disableTracking: boolean;

  public constructor(disableTracking) {
    this._disableTracking = disableTracking;
  }

  private _push(data:
    InterfaceTimetableSearch |
    InterfaceOpenLevelTwo |
    InterfaceBuyButtonClick |
    InterfacePreviousNextConnections): void {

    if (this._disableTracking) {
      return;
    }

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    if (window.dataLayer.constructor !== Array) {
      return;
    }

    window.dataLayer.push(data);
  }

  public timetableSearch = (options: InterfaceTimetableSearchOptions): void => {
    const additionalInfo = {
      accessibility: 'none',
      'connection-type': 'none',
      via: 'none',
    };

    if (options.via) {
      additionalInfo.via = options.via;
    }

    if (options.bicycle) {
      additionalInfo['connection-type'] = 'bicycle';
    }

    if (options.accessibility) {
      additionalInfo.accessibility = 'true';
    }

    const data: InterfaceTimetableSearch = {
      'additional_info': (new URLSearchParams(additionalInfo))
        .toString()
        .toLocaleLowerCase(),
      'event': 'search',
      'original_search_term': options.destination.toLocaleLowerCase(),
      'search_results': options.results,
      'search_term': options.origin.toLocaleLowerCase(),
    };

    this._push(data);
  };

  public openOrCollapseLevelTwo = (options: InterfaceOpenLevelTwoOptions): void => {
    const data: InterfaceOpenLevelTwo = {
      'additional_info': `${options.changes} - ${options.duration}`,
      'event': options.opening
        ? 'select_content'
        : 'deselect_content',
      'label': options.opening
        ? 'timetable-trip-expanded'
        : 'timetable-trip',
      'selection': options.price
        ? options.price
        : undefined,
      'text': options.title,
      'type': 'timetable_trip_list_item',
    };

    this._push(data);
  };

  public buyButtonClick = (options: InterfaceBuyButtonClickOptions): void => {
    const url = new URL(options.link);

    // remove tripId param, otherwise the link will be too long.
    url.searchParams.delete('tripId');

    const data: InterfaceBuyButtonClick = {
      'additional_info': `${options.changes} - ${options.duration}`,
      'event': 'link_click',
      'label': 'sbb-buy-link',
      'link_url': url.toString(),
      'selection': options.price
        ? options.price
        : undefined,
      'text': options.title,
      'type': 'timetable_trip_buy_link',
    };

    this._push(data);
  };

  public previousNextConnections = (options: InterfacePreviousNextConnectionsOptions): void => {
    const data: InterfacePreviousNextConnections = {
      'event': 'button_click',
      'label': options.isForPreviousConnections
        ? 'timetable-load-more-previous-trips'
        : 'timetable-load-more-next-trips',
      'type': 'secondary',
    };

    this._push(data);
  };
}
